import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  COUNTRIES,
  PRIORITY_COUNTRIES,
  REST_COUNTRIES,
  type Country,
} from '../../data/countries';
import './CountryPicker.css';

interface CountryPickerProps {
  /** ISO-2 code of the selected country, or null/empty for unselected. */
  value: string | null;
  onChange: (code: string) => void;
  /** Visible label when nothing is selected. */
  placeholder?: string;
  /** Show a chevron icon next to the trigger text. */
  showChevron?: boolean;
  className?: string;
  disabled?: boolean;
}

const codeToCountry: Map<string, Country> = new Map(
  COUNTRIES.map((c) => [c.code, c]),
);

export const CountryPicker: React.FC<CountryPickerProps> = ({
  value,
  onChange,
  placeholder = '—',
  className = '',
  disabled = false,
  showChevron = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const current = value ? codeToCountry.get(value) ?? null : null;

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Auto-focus the search field when the menu opens
  useEffect(() => {
    if (open) {
      setQuery('');
      // wait for the menu to mount
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [open]);

  const { topMatches, restMatches } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = (c: Country) =>
      !q || c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q);
    return {
      topMatches: PRIORITY_COUNTRIES.filter(matches),
      restMatches: REST_COUNTRIES.filter(matches),
    };
  }, [query]);

  const pick = (code: string) => {
    onChange(code);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={`mg-cpick ${className}`}>
      <button
        type="button"
        className={`mg-cpick__trigger ${current ? '' : 'mg-cpick__trigger--empty'}`}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="mg-cpick__current">
          {current ? current.name : placeholder}
        </span>
        {showChevron && (
          <svg
            className="mg-cpick__chev"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        )}
      </button>

      {open && (
        <div className="mg-cpick__menu" role="listbox">
          <div className="mg-cpick__search">
            <input
              ref={searchRef}
              type="text"
              className="mg-cpick__search-input"
              placeholder="Поиск…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="mg-cpick__list">
            {topMatches.length > 0 && (
              <>
                {topMatches.map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    role="option"
                    aria-selected={c.code === value}
                    className={`mg-cpick__item ${c.code === value ? 'mg-cpick__item--active' : ''}`}
                    onClick={() => pick(c.code)}
                  >
                    {c.name}
                  </button>
                ))}
                {restMatches.length > 0 && <div className="mg-cpick__divider" />}
              </>
            )}

            {restMatches.map((c) => (
              <button
                key={c.code}
                type="button"
                role="option"
                aria-selected={c.code === value}
                className={`mg-cpick__item ${c.code === value ? 'mg-cpick__item--active' : ''}`}
                onClick={() => pick(c.code)}
              >
                {c.name}
              </button>
            ))}

            {topMatches.length === 0 && restMatches.length === 0 && (
              <div className="mg-cpick__empty">Ничего не найдено</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
